import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Grid, InputAdornment, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SurveyPublicService from '../../../shared/services/survey/survey-public.service';
import { ISurveyProductAnswer, ISurveyResolveResponse } from '../../../shared/services/survey/survey.model';

//The public form for a supplier product-data survey: one block per product, three inputs each
//(unit, amount per unit, kg CO2e per unit). Each product saves as soon as its figures are
//complete, so a closed tab loses nothing; Submit finalises the whole response. The figures are
//proposals that the organisation reviews before anything changes on its product records.

interface ProductRowState {
  measurementUnitId: string;
  unitAmount: string;
  kgCarbonEmission: string;
}

interface Props {
  surveyData: ISurveyResolveResponse;
  sessionToken: string;
  /** Rows saved in a previous visit to this session, used to resume. */
  resumedRows?: { capitalGoodId: string; unitAmount: number; measurementUnitId: string; kgCarbonEmission: number }[];
  onSubmitted: () => void;
}

function fieldError(field: keyof ProductRowState, state: ProductRowState | undefined): string | null {
  if (!state) return null;
  if (field === 'measurementUnitId') {
    return state.measurementUnitId === '' ? 'Choose a unit' : null;
  }
  const raw = state[field];
  if (raw === '') return 'Enter a figure';
  const num = Number(raw);
  if (Number.isNaN(num)) return 'Enter a number';
  if (num < 0) return 'Must be zero or more';
  return null;
}

export const SurveyProductDataStep = ({ surveyData, sessionToken, resumedRows = [], onSubmitted }: Props) => {
  const surveyPublicService = useRef(new SurveyPublicService()).current;
  const goods = useMemo(() => surveyData.productGoods ?? [], [surveyData.productGoods]);
  const units = surveyData.measurementUnits ?? [];

  const [rows, setRows] = useState<Map<string, ProductRowState>>(() => {
    const initial = new Map<string, ProductRowState>();
    for (const row of resumedRows) {
      initial.set(row.capitalGoodId, {
        measurementUnitId: row.measurementUnitId ?? '',
        unitAmount: String(row.unitAmount ?? ''),
        kgCarbonEmission: String(row.kgCarbonEmission ?? ''),
      });
    }
    return initial;
  });
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Goods whose current figures have been saved to the session (superset survives resume).
  const [savedGoodIds, setSavedGoodIds] = useState<Set<string>>(new Set(resumedRows.map(r => r.capitalGoodId)));
  const savingRef = useRef<Set<string>>(new Set());

  const markTouched = (goodId: string, field: keyof ProductRowState) => {
    setTouched(prev => new Set(prev).add(`${goodId}:${field}`));
  };

  const updateRow = (capitalGoodId: string, patch: Partial<ProductRowState>) => {
    setRows(prev => {
      const next = new Map(prev);
      const existing = prev.get(capitalGoodId) ?? { measurementUnitId: '', unitAmount: '', kgCarbonEmission: '' };
      next.set(capitalGoodId, { ...existing, ...patch });
      return next;
    });
    setSavedGoodIds(prev => {
      if (!prev.has(capitalGoodId)) return prev;
      const next = new Set(prev);
      next.delete(capitalGoodId);
      return next;
    });
  };

  const isRowComplete = (state?: ProductRowState) => {
    if (!state) return false;
    const amount = Number(state.unitAmount);
    const emission = Number(state.kgCarbonEmission);
    return state.measurementUnitId !== ''
      && state.unitAmount !== '' && !Number.isNaN(amount) && amount >= 0
      && state.kgCarbonEmission !== '' && !Number.isNaN(emission) && emission >= 0;
  };

  const completeCount = useMemo(
    () => goods.filter(good => isRowComplete(rows.get(good.id))).length,
    [goods, rows],
  );
  const allComplete = goods.length > 0 && completeCount === goods.length;

  // A completed row saves itself: the backend upserts per (session, product), so a
  // closed tab keeps everything saved so far.
  useEffect(() => {
    for (const good of goods) {
      const state = rows.get(good.id);
      if (!state || !isRowComplete(state) || savedGoodIds.has(good.id) || savingRef.current.has(good.id)) continue;
      savingRef.current.add(good.id);
      surveyPublicService.saveProductAnswers(sessionToken, [{
        capitalGoodId: good.id,
        measurementUnitId: state.measurementUnitId,
        unitAmount: Number(state.unitAmount),
        kgCarbonEmission: Number(state.kgCarbonEmission),
      }])
        .then(() => {
          setSavedGoodIds(prev => new Set(prev).add(good.id));
        })
        .catch(() => {
          // The final submit re-posts everything, so a failed row save self-heals.
        })
        .finally(() => {
          savingRef.current.delete(good.id);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, goods, sessionToken]);

  const handleSubmit = async () => {
    if (!allComplete) {
      // Reveal every field's validation so the incomplete products explain themselves.
      const everything = new Set<string>();
      for (const good of goods) {
        everything.add(`${good.id}:measurementUnitId`);
        everything.add(`${good.id}:unitAmount`);
        everything.add(`${good.id}:kgCarbonEmission`);
      }
      setTouched(everything);
      const missing = goods.length - completeCount;
      setErrorMessage(`${missing} product${missing === 1 ? '' : 's'} still need${missing === 1 ? 's' : ''} figures before you can submit.`);
      return;
    }
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const answers: ISurveyProductAnswer[] = goods.map(good => {
        const state = rows.get(good.id)!;
        return {
          capitalGoodId: good.id,
          measurementUnitId: state.measurementUnitId,
          unitAmount: Number(state.unitAmount),
          kgCarbonEmission: Number(state.kgCarbonEmission),
        };
      });
      await surveyPublicService.saveProductAnswers(sessionToken, answers);
      await surveyPublicService.submitSurvey(sessionToken);
      onSubmitted();
    } catch (err: any) {
      const backendMessage = err?.response?.data?.message;
      setErrorMessage(typeof backendMessage === 'string' && backendMessage
        ? backendMessage
        : 'Submission failed. Please check your figures and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper variant='elevation' elevation={0} sx={{ p: 4 }}>
      <Typography variant='h6' sx={{ mb: 1 }}>{surveyData.name}</Typography>
      {surveyData.description && (
        <Typography color='text.secondary' sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{surveyData.description}</Typography>
      )}
      {surveyData.invitation?.recipientName && (
        <Typography sx={{ mb: 2 }}>Hi, {surveyData.invitation.recipientName}</Typography>
      )}
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        For each product below, tell us the unit it is measured in, the amount per unit, and the
        greenhouse gas emissions per unit in kg CO2e. Your figures are reviewed before they are
        used. Completed products save automatically, so you can come back and finish later.
      </Typography>
      {goods.length > 0 && (
        <Typography variant='body2' sx={{ mb: 3 }} fontWeight={600}>
          {completeCount} of {goods.length} products complete
        </Typography>
      )}

      {goods.length === 0 && (
        <Typography color='text.secondary' sx={{ py: 2 }}>
          There are no products to provide data for. Please contact the organisation that sent
          you this link.
        </Typography>
      )}

      <Stack spacing={2}>
        {goods.map((good, index) => {
          const state = rows.get(good.id);
          const complete = isRowComplete(state);
          const showError = (field: keyof ProductRowState) =>
            touched.has(`${good.id}:${field}`) ? fieldError(field, state ?? { measurementUnitId: '', unitAmount: '', kgCarbonEmission: '' }) : null;
          const unitError = showError('measurementUnitId');
          const amountError = showError('unitAmount');
          const emissionError = showError('kgCarbonEmission');
          return (
            <Paper key={good.id} variant='outlined' sx={{ p: 2.5 }}>
              <Stack direction='row' spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant='body2' color='text.secondary'>{index + 1} of {goods.length}</Typography>
                <Typography variant='subtitle1'>{good.name}</Typography>
              </Stack>
              {good.modelNumber && good.modelNumber !== good.name && (
                <Typography variant='body2' color='text.secondary'>Model number: {good.modelNumber}</Typography>
              )}
              {good.description && good.description !== good.name && (
                <Typography variant='body2' color='text.secondary'>{good.description}</Typography>
              )}
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label='Unit of measure'
                    value={state?.measurementUnitId ?? ''}
                    onChange={e => updateRow(good.id, { measurementUnitId: e.target.value })}
                    onBlur={() => markTouched(good.id, 'measurementUnitId')}
                    error={Boolean(unitError)}
                    helperText={unitError ?? 'How this product is measured'}
                  >
                    {units.map(unit => (
                      <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label='Amount per unit'
                    type='number'
                    value={state?.unitAmount ?? ''}
                    onChange={e => updateRow(good.id, { unitAmount: e.target.value })}
                    onBlur={() => markTouched(good.id, 'unitAmount')}
                    error={Boolean(amountError)}
                    helperText={amountError ?? 'For example 1, or 0.5 for half a unit'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label='Emissions per unit'
                    type='number'
                    value={state?.kgCarbonEmission ?? ''}
                    onChange={e => updateRow(good.id, { kgCarbonEmission: e.target.value })}
                    onBlur={() => markTouched(good.id, 'kgCarbonEmission')}
                    error={Boolean(emissionError)}
                    InputProps={{ endAdornment: <InputAdornment position='end'>kg CO2e</InputAdornment> }}
                    helperText={emissionError ?? 'Cradle-to-gate greenhouse gas emissions for one unit of this product'}
                  />
                </Grid>
              </Grid>
              {complete && (
                <Typography variant='caption' color='success.main' sx={{ display: 'block', mt: 1 }}>
                  {savedGoodIds.has(good.id) ? 'Complete and saved' : 'Complete'}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Stack>

      <Box sx={{ mt: 4 }}>
        {errorMessage && (
          <Typography color='error' sx={{ mb: 2 }}>{errorMessage}</Typography>
        )}
        <Stack direction='row' justifyContent='flex-end'>
          <LoadingButton
            variant='contained'
            size='large'
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            Submit product data
          </LoadingButton>
        </Stack>
      </Box>
    </Paper>
  );
};
